import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isHTMLSafe, htmlSafe } from '@ember/template'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { t, type IntlService } from 'ember-intl'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import type Student from 'teacher-ui-client/models/student'
import { differenceWith } from 'lodash'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import formatValue from 'teacher-ui-client/helpers/format-value'
import { Legend, TD, TH } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import type { StoriesWrittenHistoryData } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  student_id: string
  id: string
  firstName: string
  lastName: string
  teacherName: string
  grade_position: number
  storiesWritten: number | null
}

interface Args {
  storiesWrittenHistoryData: StoriesWrittenHistoryData
  students: Student[]
  subco: boolean
  uiScope: UIScopeName
  tooltip: string
  studentRouteName: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

interface ChartArgs {
  data: HistoryDataViewModel
  showYAxisLabels: boolean
  maxValue: number
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    opposite: true,
    max: chartOptions.maxValue,
    minorTicks: false,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = [
    {
      type: 'bar' as const,
      name: 'storiesWritten',
      data: [chartOptions.data.storiesWritten ?? 0],
    },
  ]

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [colors.oceanyBlue300],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class StoriesWrittenHistory extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  @tracked processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)

    this.processor = new DataProcessor(this)
  }

  get data() {
    return this.tableRows
  }

  get amendedTooltip() {
    const { tooltip } = this.args

    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.storiesWrittenHistoryData,
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get tableRows() {
    const stats = this.args.storiesWrittenHistoryData
    return this.args.students.filter(hasId).map((student) => {
      const statRow = stats?.find((stat) => String(stat.student_id) === student.id)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        grade_position: student.gradePosition,
        id: student.id,
        student_id: student.id,
        storiesWritten: statRow?.stories_written ?? null,
      }
    })
  }

  get maxStoriesWritten() {
    const highestTestsCompleted =
      Math.max(...this.args.storiesWrittenHistoryData.map((data) => data.stories_written)) ?? 10
    return Math.ceil(highestTestsCompleted / 5) * 5
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildChartOptions({
          data: row,
          showYAxisLabels: index === 0,
          maxValue: this.maxStoriesWritten,
        }),
      }
    })
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  <template>
    <div
      data-test-giraffe-stories-written-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-stories-written-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="overflow-x-auto print:overflow-visible">
        <table class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-oceany-blue-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "grade_position")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="grade_position" />
                  </div>
                </TH>
              {{/if}}
              <TH
                class="w-36"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "storiesWritten")}}
              >
                <div class="flex items-center justify-between font-semibold">
                  {{t "reporting.charts.storiesWritten.totalStoriesWritten" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="storiesWritten" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  data-test-stories-written-legend
                  class="border-none
                    {{if @subco 'w-1/3' 'w-1/2'}}
                    border-l-dusty-black-100 bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend @title={{t "reporting.chartTitles.storiesWrittenHistory.re" htmlSafe=true}} />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:border-t print:border-black">
            {{#each this.rowsWithChartOptions as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-stories-written-student-row={{index}}
                class="hover:cursor-pointer"
                {{on "click" (fn this.transitionToStudentRoute row.student_id)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class={{if this.showChart "max-h-20 overflow-auto"}}>{{row.teacherName}}</div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.grade_position}}
                  </TD>
                {{/if}}
                <TD data-test-stories-written class="text-center">
                  {{formatValue row.storiesWritten}}
                </TD>
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 border-l px-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-stories-written-highchart-row
                      class="h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default StoriesWrittenHistory
